import { useCallback, useEffect, useRef } from 'react'
import { resolveBotProtectionAdapter } from './registry'
import type { BotProtectionWidgetInstance, BotProtectionWidgetProps } from './types'
import { useBotProtectionRuntimeEventLogger } from './runtimeEventLogger'
import { useBotProtectionRuntimeState } from './widgetRuntimeState'
import { useBotProtectionRetryPolicy } from './widgetRetryPolicy'
import { useBotProtectionWidgetEventHandlers } from './widgetEventHandlers'

export const useBotProtectionWidget = ({
  app,
  providerId,
  clientConfig,
  onTokenChange,
  resetNonce,
  lifecycle,
  containerName,
}: BotProtectionWidgetProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetRef = useRef<BotProtectionWidgetInstance | null>(null)
  const onTokenChangeRef = useRef(onTokenChange)
  const runtimeStateRef = useRef<ReturnType<typeof useBotProtectionRuntimeState> | null>(null)
  const getRuntimeState = useCallback(
    () => runtimeStateRef.current?.getRuntimeState() ?? 'idle',
    [],
  )

  const logRuntimeEvent = useBotProtectionRuntimeEventLogger({
    app,
    containerName,
    providerId,
    getRuntimeState,
  })
  const runtimeState = useBotProtectionRuntimeState(logRuntimeEvent)
  runtimeStateRef.current = runtimeState

  const retryPolicy = useBotProtectionRetryPolicy({
    lifecycle,
    logRuntimeEvent,
    runtimeState,
    widgetRef,
  })
  const widgetEvents = useBotProtectionWidgetEventHandlers({
    logRuntimeEvent,
    onTokenChangeRef,
    retryPolicy,
    runtimeState,
  })

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange
  }, [onTokenChange])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let disposed = false
    const adapter = resolveBotProtectionAdapter(providerId)

    if (!adapter) {
      onTokenChangeRef.current('')
      runtimeState.setRuntimeState('failed', { reason: 'provider_not_supported' })
      logRuntimeEvent('error', 'bot_protection.provider_not_supported', {
        providerId,
      })
      return
    }

    runtimeState.setRuntimeState('loading_script')
    runtimeState.setRuntimeState('rendering_widget')
    void adapter
      .renderWidget({
        container,
        clientConfig,
        callbacks: widgetEvents.callbacks,
        onRuntimeEvent: widgetEvents.onRuntimeEvent,
      })
      .then((widget) => {
        if (disposed) {
          widget.destroy()
          return
        }
        widgetRef.current = widget
        runtimeState.setRuntimeState('widget_ready')
      })
      .catch((error) => {
        if (disposed) return
        onTokenChangeRef.current('')
        runtimeState.setRuntimeState('failed', { reason: 'render_failed' })
        logRuntimeEvent('error', 'bot_protection.render_failed', {
          error: error instanceof Error ? error.message : String(error),
        })
      })

    return () => {
      disposed = true
      retryPolicy.clearRetryTimer()
      onTokenChangeRef.current('')
      widgetRef.current?.destroy()
      widgetRef.current = null
      retryPolicy.resetAttempts()
      runtimeState.setRuntimeState('idle', { reason: 'cleanup' })
      container.innerHTML = ''
    }
  }, [clientConfig, logRuntimeEvent, providerId, retryPolicy, runtimeState, widgetEvents])

  useEffect(() => {
    if (resetNonce === undefined || !widgetRef.current) return
    retryPolicy.resetAttempts()
    onTokenChangeRef.current('')
    runtimeState.setRuntimeState('verifying', { reason: 'manual_reset' })
    widgetRef.current.reset()
    logRuntimeEvent('info', 'bot_protection.widget_reset_requested', {
      resetNonce,
    })
  }, [logRuntimeEvent, resetNonce, retryPolicy, runtimeState])

  return { containerRef }
}
